import { useController } from 'react-hook-form';

import { useIntl } from '@zenbusiness/application-commons-intl';

import { buildFieldName } from '#client/utils/form';

import { FIELD_NAMES, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '../../constants';
import { isPasswordValid, getPasswordErrorMessageId } from '../../utils/validations';

/**
 * Custom hook for the PasswordField component
 *
 * @param {object} options - Hook options
 * @param {string} [options.defaultValue] - Default value for the field
 * @param {string} [options.namePrefix] - Prefix for the field name
 * @param {object} [options.validation] - Additional validation rules
 * @returns {object} Field props and state
 */
export default function usePasswordField({ defaultValue = '', namePrefix, validation } = {}) {
  const { formatMessage } = useIntl();

  // TODO: Update message ids to use generic component messageKey prefixes.
  // e.g. 'shopping.component.accountFieldset.password.error.required'
  const MESSAGE_IDS = {
    required: 'shopping.page.checkout.account.form.field.password.error.required'
  };

  const defaultValidation = {
    required: formatMessage({ id: MESSAGE_IDS.required }),
    validate: (value) => {
      if (isPasswordValid(value)) {
        return true;
      }

      const errorMessageId = getPasswordErrorMessageId(value);
      return formatMessage(
        { id: errorMessageId },
        { minLength: PASSWORD_MIN_LENGTH, maxLength: PASSWORD_MAX_LENGTH }
      );
    }
  };

  const fieldName = buildFieldName(FIELD_NAMES.PASSWORD, namePrefix);

  const {
    field,
    fieldState: { error }
  } = useController({
    defaultValue,
    name: fieldName,
    rules: { ...defaultValidation, ...validation }
  });

  return {
    error,
    field: {
      ...field,
      value: field.value ?? ''
    }
  };
}
